import {
  Document,
  Page as PdfPage,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from '@react-pdf/renderer';

// Reuse the self-hosted EB Garamond family (same as the syllabus PDF).
Font.register({
  family: 'EB Garamond',
  fonts: [
    { src: '/fonts/EBGaramond-Regular.ttf' },
    { src: '/fonts/EBGaramond-Bold.ttf', fontWeight: 700 },
    { src: '/fonts/EBGaramond-Italic.ttf', fontStyle: 'italic' },
    { src: '/fonts/EBGaramond-BoldItalic.ttf', fontWeight: 700, fontStyle: 'italic' },
  ],
});

export interface TranscriptRow {
  term: string;
  code: string;
  course: string;
  faculty: string;
  credits: number;
  grade: number | null;
  /** La nota como se escribe en la escala del seminario. */
  gradeText?: string;
  finishedOn: string;
}

export interface TranscriptPdfData {
  brand: { tenantName: string; logoUrl?: string; primaryColor: string };
  student: { name: string; email: string };
  generatedOn: string;
  rows: TranscriptRow[];
  totals: { coursesCompleted: number; totalCredits: number; weightedGpa: number | null };
  labels: {
    title: string;
    watermark: string;
    draftBadge: string;
    draftNote: string;
    studentHeading: string;
    name: string;
    email: string;
    coursesCompleted: string;
    totalCredits: string;
    gpa: string;
    colTerm: string;
    colCode: string;
    colCourse: string;
    colFaculty: string;
    colCredits: string;
    colGrade: string;
    colFinished: string;
    empty: string;
    footerDisclaimer: string;
  };
}

// Column widths (sum = 100).
const COLS = {
  term: '14%',
  code: '10%',
  course: '29%',
  faculty: '20%',
  credits: '9%',
  grade: '9%',
  finished: '9%',
} as const;

const styles = StyleSheet.create({
  page: {
    fontFamily: 'EB Garamond',
    fontSize: 10,
    color: '#1f2937',
    paddingTop: 42,
    paddingBottom: 56,
    paddingHorizontal: 48,
  },
  watermark: {
    position: 'absolute',
    top: '42%',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 54,
    fontWeight: 700,
    color: '#000000',
    opacity: 0.06,
    transform: 'rotate(-45deg)',
  },
  header: { textAlign: 'center', marginBottom: 18 },
  logo: { height: 54, objectFit: 'contain', marginBottom: 6, alignSelf: 'center' },
  tenantName: { fontSize: 17, fontWeight: 700, lineHeight: 1.2 },
  brandBar: { width: 64, height: 3, borderRadius: 2, alignSelf: 'center', marginVertical: 7 },
  docTitle: { fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' },
  generatedOn: { fontSize: 9, color: '#6b7280', marginTop: 2 },
  studentBox: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 4,
    padding: 10,
    marginBottom: 14,
    flexDirection: 'row',
  },
  studentHeading: {
    fontSize: 8,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: '#6b7280',
    marginBottom: 4,
  },
  fieldLabel: { fontSize: 8, color: '#6b7280' },
  fieldValue: { fontSize: 11, fontWeight: 700 },
  table: { marginBottom: 14 },
  thead: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    borderBottomWidth: 1,
    borderBottomColor: '#9ca3af',
  },
  th: {
    fontSize: 8,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingVertical: 5,
    paddingHorizontal: 4,
  },
  tr: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#e5e7eb' },
  td: { fontSize: 9, paddingVertical: 4, paddingHorizontal: 4 },
  right: { textAlign: 'right' },
  empty: { fontSize: 10, color: '#6b7280', paddingVertical: 10 },
  totalsBox: {
    flexDirection: 'row',
    gap: 28,
    borderTopWidth: 1,
    borderTopColor: '#d1d5db',
    paddingTop: 10,
  },
  totalLabel: { fontSize: 8, color: '#6b7280' },
  totalValue: { fontSize: 14, fontWeight: 700 },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    fontSize: 8,
    textAlign: 'center',
    color: '#94a3b8',
    borderTopWidth: 0.5,
    borderTopColor: '#e2e8f0',
    paddingTop: 6,
  },
  pageNumber: { position: 'absolute', bottom: 10, right: 48, fontSize: 8, color: '#94a3b8' },
});

export function TranscriptPdfDocument({ data }: { data: TranscriptPdfData }): JSX.Element {
  const { brand, student, generatedOn, rows, totals, labels } = data;

  return (
    <Document>
      <PdfPage size="A4" style={styles.page}>
        {/* Diagonal draft watermark — repeats on every page. */}
        <Text style={styles.watermark} fixed>
          {labels.watermark}
        </Text>

        {/* Header */}
        <View style={styles.header}>
          {brand.logoUrl ? <Image src={brand.logoUrl} style={styles.logo} /> : null}
          <Text style={styles.tenantName}>{brand.tenantName}</Text>
          <View style={[styles.brandBar, { backgroundColor: brand.primaryColor }]} />
          <Text style={styles.docTitle}>{labels.title}</Text>
          <Text style={styles.generatedOn}>{generatedOn}</Text>
        </View>

        {/* Student */}
        <View style={styles.studentBox}>
          <View>
            <Text style={styles.studentHeading}>{labels.studentHeading}</Text>
            <View style={{ flexDirection: 'row', gap: 28 }}>
              <View>
                <Text style={styles.fieldLabel}>{labels.name}</Text>
                <Text style={styles.fieldValue}>{student.name}</Text>
              </View>
              {student.email ? (
                <View>
                  <Text style={styles.fieldLabel}>{labels.email}</Text>
                  <Text style={styles.fieldValue}>{student.email}</Text>
                </View>
              ) : null}
            </View>
          </View>
        </View>

        {/* Courses table */}
        <View style={styles.table}>
          <View style={styles.thead} fixed>
            <Text style={[styles.th, { width: COLS.term }]}>{labels.colTerm}</Text>
            <Text style={[styles.th, { width: COLS.code }]}>{labels.colCode}</Text>
            <Text style={[styles.th, { width: COLS.course }]}>{labels.colCourse}</Text>
            <Text style={[styles.th, { width: COLS.faculty }]}>{labels.colFaculty}</Text>
            <Text style={[styles.th, styles.right, { width: COLS.credits }]}>
              {labels.colCredits}
            </Text>
            <Text style={[styles.th, styles.right, { width: COLS.grade }]}>{labels.colGrade}</Text>
            <Text style={[styles.th, { width: COLS.finished }]}>{labels.colFinished}</Text>
          </View>

          {rows.length === 0 ? (
            <Text style={styles.empty}>{labels.empty}</Text>
          ) : (
            rows.map((r, i) => (
              <View key={i} style={styles.tr} wrap={false}>
                <Text style={[styles.td, { width: COLS.term }]}>{r.term}</Text>
                <Text style={[styles.td, { width: COLS.code }]}>{r.code}</Text>
                <Text style={[styles.td, { width: COLS.course }]}>{r.course}</Text>
                <Text style={[styles.td, { width: COLS.faculty }]}>{r.faculty || '—'}</Text>
                <Text style={[styles.td, styles.right, { width: COLS.credits }]}>{r.credits}</Text>
                <Text style={[styles.td, styles.right, { width: COLS.grade }]}>
                  {r.gradeText ?? (typeof r.grade === 'number' ? r.grade.toFixed(1) : '—')}
                </Text>
                <Text style={[styles.td, { width: COLS.finished }]}>{r.finishedOn || '—'}</Text>
              </View>
            ))
          )}
        </View>

        {/* Totals */}
        <View style={styles.totalsBox}>
          <View>
            <Text style={styles.totalLabel}>{labels.coursesCompleted}</Text>
            <Text style={styles.totalValue}>{totals.coursesCompleted}</Text>
          </View>
          <View>
            <Text style={styles.totalLabel}>{labels.totalCredits}</Text>
            <Text style={styles.totalValue}>{totals.totalCredits}</Text>
          </View>
          {totals.weightedGpa !== null ? (
            <View>
              <Text style={styles.totalLabel}>{labels.gpa}</Text>
              <Text style={styles.totalValue}>{totals.weightedGpa.toFixed(2)}</Text>
            </View>
          ) : null}
        </View>

        {/* Footer + page number (repeat every page) */}
        <Text style={styles.footer} fixed>
          {labels.footerDisclaimer}
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </PdfPage>
    </Document>
  );
}
